import React, { useCallback, useMemo } from 'react';

import { useSingleHiddenFields } from '../../../../../../hooks/useHiddenSingleFields';
import { OpenFieldSection } from '../../../../../datasets/instances/store/types';
import { fieldTypeWidths, MAX_GROUP_WIDTH } from '../../../../../admin/dataset-builder/FieldList';
import { DocumentSpecificHiddenFields, GroupDatasetField, HiddenFields } from '../../../../../datasets/store/types';
import styles from '../../ReportDefinitions.module.scss';
import { SingleField } from './SingleField';

interface GroupFieldProps {
    field: GroupDatasetField;
    groupIndex: number;
    sectionId: string;
    datasetId: number;
    parents: number;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    isLastFieldInSection?: boolean;
    parentFieldId: string;
}

export const GroupField: React.FC<GroupFieldProps> = ({ field, groupIndex, sectionId, datasetId, parents, toggleSection, getSectionOpen, hiddenFields, datasetHidden, documentSpecificHiddenFields, parentHiddenDocumentNameIds, isLastFieldInSection, parentFieldId }) => {

    const { children } = field;
    const currentHiddenDatasetFields = useMemo(() => hiddenFields[datasetId] || [], [datasetId, hiddenFields]);

    const hiddenSingleFields = useSingleHiddenFields(currentHiddenDatasetFields);

    const childFieldHidden = useCallback((id: string) => !!hiddenSingleFields.find(({ fieldId }) => fieldId === id), [hiddenSingleFields]);

    const childrenWithoutHiddenFields = useMemo(() => children.filter(({ id }) => !hiddenSingleFields.map(({ fieldId }) => fieldId).includes(id!)), [children, hiddenSingleFields]);

    const groupWidth = useMemo(() => childrenWithoutHiddenFields.reduce((acc, cur) => acc + (fieldTypeWidths[cur.type] / MAX_GROUP_WIDTH), 0), [childrenWithoutHiddenFields]);

    return (
        <div className={styles.groupFieldWrapper} data-testid={`report-form-group-field-${groupIndex}-wrapper`}>
            {children.map((field, index) => {
                const childHidden = childFieldHidden(field.id!);
                if (childHidden) {
                    return null;
                }
                const isLastFieldInGroup = index === childrenWithoutHiddenFields.length - 1;
                const width = fieldTypeWidths[field.type] / groupWidth;
                return (
                    <SingleField
                        key={field.id}
                        field={field}
                        isLastFieldInGroup={isLastFieldInGroup}
                        groupIndex={groupIndex}
                        width={width}
                        sectionId={sectionId}
                        datasetId={datasetId}
                        parents={parents}
                        toggleSection={toggleSection}
                        getSectionOpen={getSectionOpen}
                        hiddenFields={hiddenFields}
                        datasetHidden={datasetHidden}
                        documentSpecificHiddenFields={documentSpecificHiddenFields}
                        parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                        isLastFieldInSection={isLastFieldInSection}
                        parentFieldId={parentFieldId}
                    />
                );
            })}
        </div>
    );
};
